<template>
  <div class="meta-video-subtitles">
    <template v-if="subtitleList.length">
      <div class="edit-subtitle">
        <template v-if="!isEditingSubtitle">
          <span class="icon el-icon-edit" @click="onSubtitleEdit">编辑字幕</span>
          <span class="icon" @click="onReMakeSubtitles">
            <em :class="remakeSubtitleLoading ? 'el-icon-loading' : 'el-icon-refresh'"></em>
            重新生成字幕
          </span>
          <span class="icon el-icon-delete" @click="onRemoveSubtitles">清除字幕</span>
        </template>
        <template v-else>
          <span class="icon el-icon-document-checked" @click="onSaveSubtitles">保存字幕</span>
        </template>
      </div>
      <div class="subtitle-inputs">
        <div class="subtitle-group" v-for="(row, index) in subtitleList" :key="index">
          <div class="timestamps">{{ row.timerange }}</div>
          <span v-if="index === scissorsIconIndex" title="回车截断字幕" class="cut-icon el-icon-scissors"></span>
          <div class="input">
            <el-input
              v-if="isEditingSubtitle"
              v-model="row.subtitle"
              @focus="scissorsIconIndex = index"
              @blur="scissorsIconIndex = -1"
              @keyup.enter.native="onSubtitleSplit($event, index)"
            ></el-input>
            <span v-else>{{ row.subtitle }}</span>
          </div>
        </div>
      </div>
    </template>
    <div style="font-size: 12px; text-align: center" v-else>
      <span>视频无字幕</span>
      <el-button type="text" @click="onReMakeSubtitles">生成字幕</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, computed } from 'vue'
import { MessageBox } from 'element-ui'
import { timeStringToSeconds, secondsToTimestamp } from '@/common/js/utils.js'
import { updateSubtitles, remakeSubtitles } from '@/api/material.model'
const timeSeparator = ' --> '
const isEditingSubtitle = ref(false)
const scissorsIconIndex = ref(-1)
const submitLoading = ref(false)
const remakeSubtitleLoading = ref(false)

// 获取当前实例
const { proxy } = getCurrentInstance()
// eslint-disable-next-line
const props = defineProps({
  value: {
    type: Array,
    default: ''
  },
  id: {
    type: [String, Number],
    default: ''
  }
})

const subtitleList = computed({
  get() {
    return props.value
  },
  set(val) {
    emits('input', val)
  }
})

// eslint-disable-next-line
const emits = defineEmits(['subtitle-edit', 'input', 'subtitle-save'])
const onSubtitleEdit = () => {
  isEditingSubtitle.value = true
  emits('subtitle-edit')
}

const onSaveSubtitles = async () => {
  const subtitleText = subtitleList.value
    // 过滤为空字幕行
    .filter((s) => s.subtitle.trim())
    .map((s) => [s.timerange, s.subtitle].join('\n'))
    .join('\n')
  const subtitles = 'WEBVTT\n\n' + subtitleText + '\n\n'
  const data = await onSubtitleSubmit(subtitles)
  if (data) {
    proxy.$Message.success('字幕修改成功')
    // props.formItem.subtitles = subtitles
    subtitleList.value = subtitleList.value.filter((s) => s.subtitle.trim())
    emits('subtitle-save')
  }
}

const onReMakeSubtitles = () => {
  if (remakeSubtitleLoading.value) return
  MessageBox.confirm('此操作将覆盖该字幕，生成时间预计一分钟，是否继续?', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    remakeSubtitleLoading.value = true
    remakeSubtitles({ detailId: props.id })
      .then((data) => {
        if (data) {
          proxy.$Message.success(data)
          subtitleList.value = []
        }
      })
      .finally(() => {
        remakeSubtitleLoading.value = false
      })
  })
}

const onRemoveSubtitles = () => {
  MessageBox.confirm('此操作将永久删除该字幕, 是否继续?', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    const data = await onSubtitleSubmit('')

    if (data) {
      proxy.$Message.success('字幕删除成功')
      // props.formItem.subtitles = ''
      subtitleList.value = []
      isEditingSubtitle.value = false
    }
  })
}

const onSubtitleSplit = (e, index) => {
  const { timerange, subtitle } = subtitleList.value[index]
  const subtitleCount = subtitle.trim().length
  const input = e.target
  const pos = input.selectionStart

  // 转换为秒的开始结束时间
  const [startts, endts] = timerange.split(timeSeparator).map(timeStringToSeconds)
  if (pos === 0 || pos >= subtitleCount) return
  // 根据光标位置获取前后截取的字符串
  const range1 = subtitle.slice(0, pos)
  const range2 = subtitle.slice(pos)

  // 根据字数获取截取的时间点
  const splitts = startts + (endts - startts) * (range1.length / subtitleCount)
  // 截取时间点作为第一段的结束点，作为第二段的开始点，并转换为时间字符串
  const range1TimeText = [startts, splitts].map((i) => secondsToTimestamp(i)).join(timeSeparator)
  const range2TimeText = [splitts, endts].map((i) => secondsToTimestamp(i)).join(timeSeparator)

  subtitleList.value.splice(index, 1, { timerange: range1TimeText, subtitle: range1 })
  subtitleList.value.splice(index + 1, 0, { timerange: range2TimeText, subtitle: range2 })
}

const onSubtitleSubmit = (subtitles) => {
  isEditingSubtitle.value = false
  submitLoading.value = true
  return updateSubtitles({ imageDetailId: props.id, subtitles }).finally(() => (submitLoading.value = false))
}
</script>

<style scoped lang="scss">
.meta-video-subtitles {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 10px;
  height: calc(100%);
}

.edit-subtitle {
  text-align: right;
  cursor: pointer;
  font-size: 12px;

  .icon {
    margin-right: 10px;
    &:hover {
      color: #1974ff;
    }
  }
}
.subtitle-inputs {
  max-height: 220px;
  overflow: auto;
  .subtitle-group {
    margin-bottom: 12px;
    position: relative;

    .active {
      background: #d7f2ff;
    }

    .timestamps {
      color: #b5b5b5;
    }

    .cut-icon {
      position: absolute;
      right: 10px;
      top: 30px;
      z-index: 1;
      opacity: 0.4;
    }
  }
}
</style>
